class LymphivaPurchase extends HTMLElement {
  connectedCallback() {
    this.quantityInput = this.querySelector('[data-ly-quantity-input]');
    this.variantInput = this.querySelector('.product-variant-id');
    this.radios = [...this.querySelectorAll('[data-ly-bundle-radio]')];
    this.variantSelect = this.querySelector('[data-ly-variant-select]');
    this.galleryImage = this.querySelector('[data-ly-gallery-image]');
    this.galleryThumbs = [...this.querySelectorAll('[data-ly-gallery-thumb]')];
    this.galleryRail = this.querySelector('[data-ly-gallery-rail]');
    this.galleryThumbTrack = this.querySelector('[data-ly-gallery-thumbs]');
    this.galleryPrevious = this.querySelector('[data-ly-gallery-previous]');
    this.galleryNext = this.querySelector('[data-ly-gallery-next]');
    this.sellingPlanRadios = [...this.querySelectorAll('[data-ly-selling-plan]')];
    this.subscriptionRadio = this.querySelector('[data-ly-subscription-radio]');
    this.planOptions = [...this.querySelectorAll('[data-ly-plan-option]')];
    this.planCopy = this.querySelector('[data-ly-plan-copy]');
    this.quickPrice = this.querySelector('[data-ly-quick-price]');
    this.quickFrequency = this.querySelector('[data-ly-quick-frequency]');
    this.submitButton = this.querySelector('.product-form__submit');
    this.submitLabel = this.querySelector('[data-ly-add-to-cart-label]');
    this.optionPrices = [...this.querySelectorAll('[data-ly-option-price]')];
    this.radios.forEach((radio) => radio.addEventListener('change', () => this.updateQuantity(radio)));
    this.sellingPlanRadios.forEach((radio) => radio.addEventListener('change', () => this.updatePurchaseSummary()));
    this.variantSelect?.addEventListener('change', () => this.updateVariant());
    this.galleryThumbs.forEach((thumb) => thumb.addEventListener('click', () => this.showGalleryImage(thumb)));
    this.galleryPrevious?.addEventListener('click', () => this.scrollGalleryThumbs(-1));
    this.galleryNext?.addEventListener('click', () => this.scrollGalleryThumbs(1));
    this.galleryThumbTrack?.addEventListener('scroll', () => this.updateGalleryRail(), { passive: true });
    this.galleryResizeObserver = this.galleryThumbTrack && 'ResizeObserver' in window
      ? new ResizeObserver(() => this.updateGalleryRail())
      : null;
    this.galleryResizeObserver?.observe(this.galleryThumbTrack);
    requestAnimationFrame(() => this.updateGalleryRail());
    const quizApplied = this.applyQuizParameters();
    if (!quizApplied) this.applyDefaultSelection();
    const selected = this.radios.find((radio) => radio.checked);
    if (selected) this.updateQuantity(selected);
    if (this.variantSelect) this.updateVariant();
    this.updatePurchaseSummary();
    this.updateArrivalDate();
  }

  updateQuantity(radio) {
    if (!this.quantityInput) return;
    this.quantityInput.value = radio.dataset.cartQuantity || radio.dataset.quantity || '1';
    this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    if (this.variantInput && radio.dataset.variantId) {
      this.variantInput.value = radio.dataset.variantId;
      this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
    this.syncSellingPlanForBundle(radio);
    this.updatePurchaseSummary();
  }

  applyQuizParameters() {
    const params = new URLSearchParams(window.location.search);
    if (params.get('quiz_source') !== 'wellness') return false;
    const bundleValue = params.get('quiz_bundle');
    const purchaseValue = params.get('quiz_purchase');
    if (!['1', '3', '5'].includes(bundleValue) || !['one_time', 'subscription'].includes(purchaseValue)) return false;

    const bundle = this.radios.find((radio) => radio.dataset.quantity === bundleValue);
    if (!bundle) return false;
    this.radios.forEach((radio) => { radio.checked = radio === bundle; });
    this.syncSellingPlanForBundle(bundle);

    const desiredPlan = purchaseValue === 'subscription'
      ? this.subscriptionRadio
      : this.sellingPlanRadios.find((radio) => !radio.value);
    if (desiredPlan) {
      this.sellingPlanRadios.forEach((radio) => { radio.checked = radio === desiredPlan; });
    }
    this.dataset.quizSource = 'wellness';
    window.setTimeout(() => this.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
    return true;
  }

  applyDefaultSelection() {
    const defaultBundle = this.radios.find((radio) => radio.hasAttribute('data-ly-default-bundle'));
    if (defaultBundle) this.radios.forEach((radio) => { radio.checked = radio === defaultBundle; });
    const defaultPurchase = this.sellingPlanRadios.find((radio) => radio.hasAttribute('data-ly-default-purchase'));
    if (defaultPurchase) this.sellingPlanRadios.forEach((radio) => { radio.checked = radio === defaultPurchase; });
  }

  syncSellingPlanForBundle(bundle) {
    if (!this.subscriptionRadio || !this.planOptions.length || !bundle) return;
    const cadence = ({ 1: 4, 3: 12, 5: 20 })[Number(bundle.dataset.quantity)];
    const variantPlans = this.planOptions.filter((option) => option.dataset.variantId === bundle.dataset.variantId);
    const exact = variantPlans.find((option) => {
      const match = option.dataset.planName?.match(/(\d+)\s*(?:week|wk)/i);
      return Number(match?.[1]) === cadence;
    });
    const selected = exact || variantPlans[0];
    if (!selected) {
      this.subscriptionRadio.value = '';
      return;
    }
    this.subscriptionRadio.value = selected.dataset.planId || '';
    this.subscriptionRadio.dataset.price = selected.dataset.price || bundle.dataset.oneTimePrice || '0';
    this.subscriptionRadio.dataset.comparePrice = selected.dataset.comparePrice || bundle.dataset.oneTimePrice || '0';
    if (this.planCopy) {
      this.planCopy.textContent = 'Renews every 8 weeks until canceled.';
    }
  }

  updateVariant() {
    if (!this.variantInput || !this.variantSelect) return;
    this.variantInput.value = this.variantSelect.value;
    this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  showGalleryImage(thumb) {
    if (!this.galleryImage || !thumb.dataset.src) return;
    this.galleryImage.src = thumb.dataset.src;
    if (thumb.dataset.srcset) {
      this.galleryImage.srcset = thumb.dataset.srcset;
      this.galleryImage.sizes = '(min-width: 990px) 42vw, 88vw';
    } else {
      this.galleryImage.removeAttribute('srcset');
      this.galleryImage.removeAttribute('sizes');
    }
    this.galleryImage.alt = thumb.dataset.alt || '';
    this.galleryThumbs.forEach((item) => {
      const active = item === thumb;
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
    thumb.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
  }

  scrollGalleryThumbs(direction) {
    if (!this.galleryThumbTrack) return;
    const horizontal = window.matchMedia('(max-width: 749px)').matches;
    const amount = horizontal ? this.galleryThumbTrack.clientWidth * 0.75 : this.galleryThumbTrack.clientHeight * 0.75;
    this.galleryThumbTrack.scrollBy({
      left: horizontal ? direction * amount : 0,
      top: horizontal ? 0 : direction * amount,
      behavior: 'smooth',
    });
  }

  updateGalleryRail() {
    if (!this.galleryThumbTrack) return;
    const horizontal = window.matchMedia('(max-width: 749px)').matches;
    const position = horizontal ? this.galleryThumbTrack.scrollLeft : this.galleryThumbTrack.scrollTop;
    const visible = horizontal ? this.galleryThumbTrack.clientWidth : this.galleryThumbTrack.clientHeight;
    const total = horizontal ? this.galleryThumbTrack.scrollWidth : this.galleryThumbTrack.scrollHeight;
    const canScrollBefore = position > 2;
    const canScrollAfter = position + visible < total - 2;
    this.galleryPrevious?.toggleAttribute('disabled', !canScrollBefore);
    this.galleryNext?.toggleAttribute('disabled', !canScrollAfter);
    this.galleryRail?.classList.toggle('can-scroll-before', canScrollBefore);
    this.galleryRail?.classList.toggle('can-scroll-after', canScrollAfter);
  }

  disconnectedCallback() {
    this.galleryResizeObserver?.disconnect();
  }

  formatMoney(cents) {
    const currency = window.Shopify?.currency?.active || 'USD';
    const locale = document.documentElement.lang || 'en-US';
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
  }

  updateArrivalDate() {
    const output = this.querySelector('[data-ly-arrival-date]');
    if (!output) return;
    const arrival = new Date();
    let businessDays = 0;
    while (businessDays < 3) {
      arrival.setDate(arrival.getDate() + 1);
      const weekday = arrival.getDay();
      if (weekday !== 0 && weekday !== 6) businessDays += 1;
    }
    const day = arrival.getDate();
    const remainder = day % 100;
    const suffix = remainder >= 11 && remainder <= 13
      ? 'th'
      : ({ 1: 'st', 2: 'nd', 3: 'rd' }[day % 10] || 'th');
    const month = new Intl.DateTimeFormat(document.documentElement.lang || 'en-US', { month: 'long' }).format(arrival);
    output.textContent = `${month} ${day}${suffix}`;
    output.dateTime = arrival.toISOString().slice(0, 10);
  }

  updatePurchaseSummary() {
    const bundle = this.radios.find((radio) => radio.checked) || this.radios[0];
    if (!bundle) return;
    const plan = this.sellingPlanRadios.find((radio) => radio.checked);
    const subscriptionSelected = Boolean(plan?.value);

    const planForOption = (option) => {
      const matching = this.planOptions.filter((planOption) => planOption.dataset.variantId === option.dataset.variantId);
      if (!matching.length) return null;
      const selectedName = this.planOptions.find((planOption) => planOption.dataset.planId === plan?.value)?.dataset.planName;
      return matching.find((planOption) => planOption.dataset.planName === selectedName) || matching[0];
    };

    this.optionPrices.forEach((option) => {
      const allocation = subscriptionSelected ? planForOption(option) : null;
      const oneTime = Number(option.dataset.oneTimePrice || 0);
      const current = allocation ? Number(allocation.dataset.price || oneTime) : oneTime;
      const merchantCompare = Number(option.dataset.comparePrice || oneTime);
      const allocationCompare = allocation ? Number(allocation.dataset.comparePrice || oneTime) : merchantCompare;
      const compare = subscriptionSelected ? Math.max(oneTime, allocationCompare) : merchantCompare;
      const currentNode = option.querySelector('[data-ly-option-current]');
      const compareNode = option.querySelector('[data-ly-option-compare]');
      const quantity = Number(option.dataset.quantity || 1);
      const perBottleDetail = option.closest('.ly-purchase-option')?.querySelector('[data-ly-per-bottle]');
      const perBottleCurrent = perBottleDetail?.querySelector('[data-ly-per-bottle-current]');
      const perBottleCompare = perBottleDetail?.querySelector('[data-ly-per-bottle-compare]');
      if (currentNode) currentNode.textContent = this.formatMoney(current);
      if (compareNode) {
        compareNode.textContent = this.formatMoney(compare);
        compareNode.hidden = compare <= current;
      }
      if (perBottleCurrent) {
        perBottleCurrent.textContent = `${this.formatMoney(current / quantity)} per bottle`;
      }
      if (perBottleCompare) {
        perBottleCompare.textContent = this.formatMoney(oneTime / quantity);
        perBottleCompare.hidden = !subscriptionSelected || current >= oneTime;
      }
    });

    const subscriptionAllocation = planForOption(bundle);
    const selectedAllocation = subscriptionSelected ? subscriptionAllocation : null;
    const oneTimeTotal = Number(bundle.dataset.oneTimePrice || 0);
    const subscriptionTotal = Number(subscriptionAllocation?.dataset.price || oneTimeTotal);
    const total = selectedAllocation ? Number(selectedAllocation.dataset.price || oneTimeTotal) : oneTimeTotal;
    const merchantCompare = Number(bundle.dataset.comparePrice || oneTimeTotal);
    const allocationCompare = selectedAllocation ? Number(selectedAllocation.dataset.comparePrice || oneTimeTotal) : merchantCompare;
    const regularTotal = subscriptionSelected ? Math.max(oneTimeTotal, allocationCompare) : merchantCompare;
    const compare = regularTotal > total ? ` <s>${this.formatMoney(regularTotal)}</s>` : '';
    const formattedTotal = this.formatMoney(total);
    const frequencyLabel = subscriptionSelected ? 'Renews every 8 weeks' : 'One-time';
    const selectedQuantity = Number(bundle.dataset.quantity || 1);
    const perBottle = this.formatMoney(total / selectedQuantity);
    const purchaseType = subscriptionSelected ? 'subscription' : 'one_time';
    const oneTimePlanPrice = this.querySelector('[data-ly-plan-one-time-price]');
    const subscriptionPlanPrice = this.querySelector('[data-ly-plan-subscription-price]');
    if (oneTimePlanPrice) oneTimePlanPrice.textContent = this.formatMoney(oneTimeTotal);
    if (subscriptionPlanPrice) subscriptionPlanPrice.textContent = this.formatMoney(subscriptionTotal);
    if (this.quickPrice) this.quickPrice.textContent = formattedTotal;
    if (this.quickFrequency) this.quickFrequency.textContent = frequencyLabel;
    if (this.submitLabel && !this.submitButton?.disabled) {
      this.submitLabel.textContent = 'Add To Cart';
    }
    this.dataset.bundleQuantity = String(selectedQuantity);
    this.dataset.purchaseType = purchaseType;
    this.dataset.displayedPrice = formattedTotal;
    this.dataset.displayedPriceCents = String(total);
    document.dispatchEvent(new CustomEvent('lymphiva:purchase-summary', {
      detail: {
        price: formattedTotal,
        frequency: frequencyLabel,
        bundleQuantity: selectedQuantity,
        purchaseType,
        priceCents: total,
        perBottle,
      },
    }));
  }
}

if (!customElements.get('lymphiva-purchase')) {
  customElements.define('lymphiva-purchase', LymphivaPurchase);
}

class LymphivaOnceVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    if (!this.video) return;

    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (
      connection?.saveData ||
      ['slow-2g', '2g', '3g'].includes(connection?.effectiveType) ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) return;

    this.video.loop = true;
    this.video.defaultMuted = true;
    this.video.muted = true;
    this.video.playsInline = true;
    this.video.setAttribute('muted', '');
    this.video.setAttribute('playsinline', '');
    this.source = window.matchMedia('(max-width: 749px)').matches
      ? this.dataset.mobileSrc
      : this.dataset.desktopSrc;
    if (!this.source) return;

    const showPlayingVideo = () => {
      this.classList.remove('is-attempting');
      this.classList.add('is-ready');
    };
    const showFallback = () => {
      this.classList.remove('is-attempting', 'is-ready');
    };
    const releaseVideo = () => {
      showFallback();
      this.video.pause();
      this.video.removeAttribute('src');
      this.video.load();
    };
    const loadAndPlayLoop = () => {
      if (document.visibilityState !== 'visible') {
        document.addEventListener('visibilitychange', loadAndPlayLoop, { once: true });
        return;
      }
      const heroBounds = this.getBoundingClientRect();
      if (heroBounds.bottom <= 0 || heroBounds.top >= window.innerHeight) return;

      this.classList.add('is-attempting');
      this.video.preload = 'auto';
      this.video.src = this.source;
      this.video.load();

      const abandonTimer = window.setTimeout(() => {
        if (!this.classList.contains('is-ready')) releaseVideo();
      }, 4500);
      this.video.addEventListener('playing', () => window.clearTimeout(abandonTimer), { once: true });
      const playRequest = this.video.play();
      if (playRequest !== undefined) playRequest.catch(releaseVideo);
    };

    this.video.addEventListener('playing', showPlayingVideo, { once: true });
    this.video.addEventListener('error', releaseVideo, { once: true });

    let started = false;
    const start = () => {
      if (started) return;
      started = true;
      window.requestAnimationFrame(() => window.requestAnimationFrame(loadAndPlayLoop));
    };
    const poster = this.closest('.ly-hero__media')?.querySelector('picture img');
    if (!poster) start();
    else if (typeof poster.decode === 'function') poster.decode().then(start, start);
    else if (poster.complete) start();
    else poster.addEventListener('load', start, { once: true });
  }
}

if (!customElements.get('lymphiva-once-video')) {
  customElements.define('lymphiva-once-video', LymphivaOnceVideo);
}

class LymphivaTestimonials extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('.ly-testimonials__viewport');
    this.track = this.querySelector('[data-ly-testimonial-track]');
    this.slides = [...this.querySelectorAll('[data-ly-testimonial-slide]')];
    this.dots = [...this.querySelectorAll('[data-ly-testimonial-dot]')];
    this.audioButtons = [...this.querySelectorAll('[data-ly-testimonial-audio-button]')];
    this.gridLayout = this.hasAttribute('data-grid-layout');
    this.nativeSwipe = this.hasAttribute('data-native-swipe');
    this.showMoreButton = this.querySelector('[data-ly-testimonial-show-more]');
    this.gridExpanded = false;
    if (!this.track || this.slides.length === 0) return;

    this.index = 0;
    this.desktopMedia = window.matchMedia('(min-width: 990px)');
    this.layoutHandler = () => this.gridLayout ? this.updateGridVisibility() : this.show(this.index, false);
    this.desktopMedia.addEventListener('change', this.layoutHandler);
    this.querySelector('[data-ly-testimonial-prev]')?.addEventListener('click', () => this.show(this.index - 1));
    this.querySelector('[data-ly-testimonial-next]')?.addEventListener('click', () => this.show(this.index + 1));
    this.dots.forEach((dot) => dot.addEventListener('click', () => this.show(Number(dot.dataset.lyTestimonialDot))));
    if (this.nativeSwipe) {
      this.track.addEventListener('scroll', () => {
        if (this.desktopMedia?.matches || this.scrollTicking) return;
        this.scrollTicking = true;
        requestAnimationFrame(() => {
          const trackLeft = this.track.getBoundingClientRect().left;
          const closest = this.slides.reduce((best, slide, index) => {
            const distance = Math.abs(slide.getBoundingClientRect().left - trackLeft);
            return distance < best.distance ? { index, distance } : best;
          }, { index: this.index, distance: Infinity });
          if (closest.index !== this.index) this.setTestimonialIndex(closest.index);
          this.scrollTicking = false;
        });
      }, { passive: true });
    }
    this.showMoreButton?.addEventListener('click', () => {
      this.gridExpanded = true;
      this.showMoreButton.setAttribute('aria-expanded', 'true');
      this.updateGridVisibility();
    });
    this.audioButtons.forEach((button) => {
      const slide = button.closest('[data-ly-testimonial-slide]');
      const audio = slide?.querySelector('[data-ly-testimonial-audio]');
      if (!audio) return;
      button.addEventListener('click', () => this.toggleAudio(button, audio));
      audio.addEventListener('playing', () => {
        this.setAudioLoading(button, false);
        this.setAudioState(button, true);
      });
      audio.addEventListener('waiting', () => this.setAudioLoading(button, true));
      audio.addEventListener('ended', () => {
        this.setAudioLoading(button, false);
        this.setAudioState(button, false);
      });
      audio.addEventListener('pause', () => {
        this.setAudioLoading(button, false);
        this.setAudioState(button, false);
      });
      audio.addEventListener('error', () => {
        this.setAudioLoading(button, false);
        this.setAudioState(button, false);
      });
      audio.addEventListener('loadedmetadata', () => this.updateAudioProgress(button, audio));
      audio.addEventListener('timeupdate', () => this.updateAudioProgress(button, audio));
    });
    this.resizeObserver = new ResizeObserver(() => this.syncHeight());
    this.slides.forEach((slide) => this.resizeObserver.observe(slide));
    if (this.gridLayout) this.updateGridVisibility();
    else this.show(0, false);
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    this.desktopMedia?.removeEventListener('change', this.layoutHandler);
  }

  syncHeight() {
    if (this.gridLayout) {
      this.viewport?.style.removeProperty('height');
      return;
    }
    if (this.desktopMedia?.matches) {
      this.viewport?.style.removeProperty('height');
      return;
    }
    const activeSlide = this.slides?.[this.index];
    if (!this.viewport || !activeSlide) return;
    this.viewport.style.height = `${activeSlide.offsetHeight + 2}px`;
  }

  updateGridVisibility() {
    if (!this.gridLayout) return;
    const shouldCollapse = !this.desktopMedia?.matches && !this.gridExpanded && this.slides.length > 3;
    this.slides.forEach((slide, index) => {
      const hidden = shouldCollapse && index >= 3;
      slide.classList.toggle('is-mobile-hidden', hidden);
      slide.setAttribute('aria-hidden', hidden ? 'true' : 'false');
    });
    const showControl = this.slides.length > 3;
    this.showMoreButton?.classList.toggle('is-visible', showControl);
    if (this.showMoreButton) this.showMoreButton.hidden = !showControl;
    this.track.style.removeProperty('transform');
    this.viewport?.style.removeProperty('height');
  }

  setAudioState(button, playing) {
    button.classList.toggle('is-playing', playing);
    button.setAttribute('aria-pressed', playing ? 'true' : 'false');
    const currentLabel = button.getAttribute('aria-label') || '';
    button.setAttribute('aria-label', playing ? currentLabel.replace(/^Play/, 'Pause') : currentLabel.replace(/^Pause/, 'Play'));
    const label = button.querySelector('[data-ly-testimonial-audio-label]');
    if (label) label.textContent = playing ? 'Pause' : 'Listen';
  }

  setAudioLoading(button, loading) {
    button.classList.toggle('is-loading', loading);
    if (loading) button.setAttribute('aria-busy', 'true');
    else button.removeAttribute('aria-busy');
    const label = button.querySelector('[data-ly-testimonial-audio-label]');
    if (label && loading) label.textContent = 'Loading audio';
  }

  updateAudioProgress(button, audio) {
    const progress = Number.isFinite(audio.duration) && audio.duration > 0
      ? Math.min(100, Math.max(0, (audio.currentTime / audio.duration) * 100))
      : 0;
    button.style.setProperty('--ly-audio-progress', `${progress}%`);
  }

  pauseAllAudio(except = null) {
    this.audioButtons.forEach((button) => {
      const audio = button.closest('[data-ly-testimonial-slide]')?.querySelector('[data-ly-testimonial-audio]');
      if (!audio || audio === except) return;
      audio.pause();
      this.setAudioLoading(button, false);
      this.setAudioState(button, false);
    });
  }

  toggleAudio(button, audio) {
    if (!audio.paused) {
      audio.pause();
      return;
    }

    this.pauseAllAudio(audio);
    this.setAudioLoading(button, true);
    audio.play().catch(() => {
      this.setAudioLoading(button, false);
      this.setAudioState(button, false);
    });
  }

  show(nextIndex, animate = true) {
    if (this.gridLayout) {
      this.updateGridVisibility();
      return;
    }
    this.pauseAllAudio();
    this.index = (nextIndex + this.slides.length) % this.slides.length;
    if (this.desktopMedia?.matches) {
      this.track.style.removeProperty('transform');
      this.viewport?.style.removeProperty('height');
      this.slides.forEach((slide) => slide.setAttribute('aria-hidden', 'false'));
      return;
    }
    if (this.nativeSwipe) {
      const slide = this.slides[this.index];
      const left = slide ? slide.offsetLeft - this.track.offsetLeft : 0;
      this.track.scrollTo({ left, behavior: animate && !window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'auto' });
      this.setTestimonialIndex(this.index);
      return;
    }
    if (!animate) this.track.style.transition = 'none';
    this.track.style.transform = `translate3d(-${this.index * 100}%, 0, 0)`;
    if (!animate) requestAnimationFrame(() => this.track.style.removeProperty('transition'));
    requestAnimationFrame(() => this.syncHeight());

    this.slides.forEach((slide, index) => slide.setAttribute('aria-hidden', index === this.index ? 'false' : 'true'));
    this.dots.forEach((dot, index) => {
      const active = index === this.index;
      dot.classList.toggle('is-active', active);
      if (active) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }

  setTestimonialIndex(index) {
    this.index = index;
    this.slides.forEach((slide, slideIndex) => slide.setAttribute('aria-hidden', slideIndex === index ? 'false' : 'true'));
    this.dots.forEach((dot, dotIndex) => {
      const active = dotIndex === index;
      dot.classList.toggle('is-active', active);
      if (active) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
    requestAnimationFrame(() => this.syncHeight());
  }
}

if (!customElements.get('lymphiva-testimonials')) {
  customElements.define('lymphiva-testimonials', LymphivaTestimonials);
}

class LymphivaCardCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-ly-card-track]');
    this.cards = [...this.querySelectorAll('[data-ly-card]')];
    this.dotsRoot = this.querySelector('[data-ly-card-dots]');
    if (!this.track || !this.cards.length || !this.dotsRoot) return;
    this.index = 0;
    this.dots = this.cards.map((card, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.setAttribute('aria-label', `Show reason ${index + 1}`);
      dot.addEventListener('click', () => this.show(index));
      this.dotsRoot.appendChild(dot);
      return dot;
    });
    this.querySelector('[data-ly-card-prev]')?.addEventListener('click', () => this.show(this.index - 1));
    this.querySelector('[data-ly-card-next]')?.addEventListener('click', () => this.show(this.index + 1));
    this.track.addEventListener('scroll', () => this.onScroll(), { passive: true });
    this.show(0, false);
  }

  show(nextIndex, animate = true) {
    this.index = (nextIndex + this.cards.length) % this.cards.length;
    const card = this.cards[this.index];
    const left = card.offsetLeft - this.track.offsetLeft;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollTo({ left, behavior: animate && !reduce ? 'smooth' : 'auto' });
    this.updateDots();
  }

  onScroll() {
    if (this.scrollTicking) return;
    this.scrollTicking = true;
    requestAnimationFrame(() => {
      const trackLeft = this.track.getBoundingClientRect().left;
      const closest = this.cards.reduce((best, card, index) => {
        const distance = Math.abs(card.getBoundingClientRect().left - trackLeft);
        return distance < best.distance ? { index, distance } : best;
      }, { index: this.index, distance: Infinity });
      this.index = closest.index;
      this.updateDots();
      this.scrollTicking = false;
    });
  }

  updateDots() {
    this.dots.forEach((dot, index) => {
      const active = index === this.index;
      dot.classList.toggle('is-active', active);
      if (active) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }
}

if (!customElements.get('lymphiva-card-carousel')) {
  customElements.define('lymphiva-card-carousel', LymphivaCardCarousel);
}

class LymphivaIngredientFlips extends HTMLElement {
  connectedCallback() {
    this.cards = [...this.querySelectorAll('.ly-inside-card')];
    this.cards.forEach((card) => {
      card.addEventListener('click', () => {
        const willOpen = card.getAttribute('aria-pressed') !== 'true';
        this.cards.forEach((item) => {
          item.setAttribute('aria-pressed', 'false');
          item.removeAttribute('data-hover-locked');
        });
        card.setAttribute('aria-pressed', willOpen ? 'true' : 'false');
        if (!willOpen) card.setAttribute('data-hover-locked', 'true');
      });

      card.addEventListener('pointerleave', () => card.removeAttribute('data-hover-locked'));
    });

    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      this.cards.forEach((card) => card.setAttribute('aria-pressed', 'false'));
      this.querySelector('.ly-inside-card:focus')?.blur();
    });
  }
}

if (!customElements.get('lymphiva-ingredient-flips')) {
  customElements.define('lymphiva-ingredient-flips', LymphivaIngredientFlips);
}

class LymphivaBenefits extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[data-ly-benefit-tab]')];
    this.panels = [...this.querySelectorAll('[data-ly-benefit-panel]')];
    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.show(index));
      tab.addEventListener('keydown', (event) => this.onKeydown(event, index));
    });
  }

  show(index, focus = false) {
    this.tabs.forEach((tab, tabIndex) => {
      const active = tabIndex === index;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.tabIndex = active ? 0 : -1;
      if (active && focus) tab.focus();
    });
    this.panels.forEach((panel, panelIndex) => {
      const active = panelIndex === index;
      panel.classList.toggle('is-active', active);
      panel.hidden = !active;
    });
  }

  onKeydown(event, index) {
    if (!['ArrowDown', 'ArrowUp', 'ArrowRight', 'ArrowLeft'].includes(event.key)) return;
    event.preventDefault();
    const direction = event.key === 'ArrowDown' || event.key === 'ArrowRight' ? 1 : -1;
    this.show((index + direction + this.tabs.length) % this.tabs.length, true);
  }
}

if (!customElements.get('lymphiva-benefits')) {
  customElements.define('lymphiva-benefits', LymphivaBenefits);
}

class LymphivaStickyCta extends HTMLElement {
  connectedCallback() {
    const targetId = this.dataset.target;
    this.target = targetId ? document.getElementById(targetId) : null;
    const visibilityTargetId = this.dataset.visibilityTarget;
    this.visibilityTarget = visibilityTargetId ? document.getElementById(visibilityTargetId) : this.target;
    this.link = this.querySelector('a[href^="#"]');
    this.price = this.querySelector('[data-ly-sticky-price]');
    this.frequency = this.querySelector('[data-ly-sticky-frequency]');
    this.onPurchaseSummary = (event) => {
      if (this.price) this.price.textContent = event.detail?.price || '';
      if (this.frequency) this.frequency.textContent = event.detail?.frequency || '';
    };
    document.addEventListener('lymphiva:purchase-summary', this.onPurchaseSummary);
    const currentPrice = document.querySelector('[data-ly-quick-price]')?.textContent;
    const currentFrequency = document.querySelector('[data-ly-quick-frequency]')?.textContent;
    if (this.price && currentPrice) this.price.textContent = currentPrice;
    if (this.frequency && currentFrequency) this.frequency.textContent = currentFrequency;
    this.onLinkClick = (event) => {
      event.preventDefault();
      this.setVisible(false);
      if (!this.target) return;

      const targetRect = this.target.getBoundingClientRect();
      const bottomGutter = 24;
      const destination = window.scrollY
        + targetRect.top
        - window.innerHeight
        + targetRect.height
        + bottomGutter;

      window.scrollTo({
        top: Math.max(0, destination),
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      });
    };
    this.link?.addEventListener('click', this.onLinkClick);
    if (!this.visibilityTarget || !('IntersectionObserver' in window)) return;

    this.observer = new IntersectionObserver(([entry]) => {
      const hasPassedPurchase = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
      this.setVisible(hasPassedPurchase);
    }, { threshold: 0.02 });
    this.observer.observe(this.visibilityTarget);
  }

  setVisible(visible) {
    this.classList.toggle('is-visible', visible);
    this.setAttribute('aria-hidden', visible ? 'false' : 'true');
    document.body.classList.toggle('ly-product-sticky-visible', visible);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.link?.removeEventListener('click', this.onLinkClick);
    document.removeEventListener('lymphiva:purchase-summary', this.onPurchaseSummary);
    document.body.classList.remove('ly-product-sticky-visible');
  }
}

if (!customElements.get('lymphiva-sticky-cta')) {
  customElements.define('lymphiva-sticky-cta', LymphivaStickyCta);
}

class LymphivaScrollReveal extends HTMLElement {
  connectedCallback() {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion || !('IntersectionObserver' in window)) {
      this.classList.add('is-revealed');
      return;
    }

    this.classList.add('is-animated');
    this.observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      this.classList.add('is-revealed');
      this.observer.disconnect();
    }, { threshold: 0.3, rootMargin: '0px 0px -8%' });
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }
}

if (!customElements.get('lymphiva-scroll-reveal')) {
  customElements.define('lymphiva-scroll-reveal', LymphivaScrollReveal);
}

class LymphivaReviewCta extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.ly-pdp-reviews');
    this.syncVisibility();
    if (!this.section || !('MutationObserver' in window)) return;
    this.observer = new MutationObserver(() => this.syncVisibility());
    this.observer.observe(this.section, { childList: true, subtree: true, attributes: true });
  }

  hasPublishedReviews() {
    if (Number(this.dataset.reviewCount || 0) > 0) return true;
    if (!this.section) return false;
    const widget = this.section.querySelector('.jdgm-rev-widg');
    const widgetCount = Number(widget?.dataset.numberOfReviews || widget?.getAttribute('data-number-of-reviews') || 0);
    return widgetCount > 0 || this.section.querySelectorAll('.jdgm-rev').length > 0;
  }

  syncVisibility() {
    this.hidden = !this.hasPublishedReviews();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }
}

if (!customElements.get('lymphiva-review-cta')) {
  customElements.define('lymphiva-review-cta', LymphivaReviewCta);
}

const publishLymphivaEvent = (name, payload) => {
  const privacy = window.Shopify?.customerPrivacy;
  const allowed = !privacy?.analyticsProcessingAllowed || privacy.analyticsProcessingAllowed();
  if (!allowed) return;
  if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event: name, ...payload });
  if (typeof window.Shopify?.analytics?.publish === 'function') {
    Promise.resolve(window.Shopify.analytics.publish(name, payload)).catch(() => {});
  }
};

if (!window.__lymphivaHeroPurchaseScrollBound) {
  window.__lymphivaHeroPurchaseScrollBound = true;
  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const cta = event.target.closest('.ly-hero__cta[href^="#"]');
    const targetId = cta?.getAttribute('href');
    const target = targetId && document.querySelector(targetId);
    if (!target) return;

    event.preventDefault();
    target.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'start',
    });
    window.history.replaceState(null, '', targetId);
  });
}

if (!window.__lymphivaPdpCtaTrackingBound) {
  window.__lymphivaPdpCtaTrackingBound = true;
  document.addEventListener('click', (event) => {
    const cta = event.target.closest('[data-ly-pdp-cta-source]');
    if (!cta) return;
    const purchase = document.querySelector('lymphiva-purchase[data-pdp-purchase="true"]');
    if (!purchase) return;
    if (cta.matches('a[href="#lymphiva-purchase"]')) {
      document.querySelector('lymphiva-sticky-cta')?.setVisible(false);
    }
    publishLymphivaEvent('lymphiva_pdp_cta_click', {
      source: cta.dataset.lyPdpCtaSource || 'unknown',
      bundle_quantity: Number(purchase?.dataset.bundleQuantity || 0),
      purchase_type: purchase?.dataset.purchaseType || 'unknown',
      displayed_price: purchase?.dataset.displayedPrice || '',
      displayed_price_cents: Number(purchase?.dataset.displayedPriceCents || 0),
    });
  });
}

document.querySelectorAll('details > summary').forEach((summary) => {
  const details = summary.parentElement;
  const syncExpanded = () => summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
  syncExpanded();
  details.addEventListener('toggle', syncExpanded);
});
